import { CONTENT_FITNESS_VERIFICATION_STATE } from "contracts/enums/content-fitness";
import { healthState, PAGE_THRESHOLDS } from "contracts/report/health";
import { For, Show } from "solid-js";
import { healthVar } from "../../ui/health";
import { coverageLabel } from "./clientLanguage";
import {
  countByVerificationState,
  type PageContentFitness,
  pageContentFitnessDimensions,
} from "./pageContentFitnessModel";

export function ContentFitnessScore(props: {
  contentFitness: PageContentFitness;
}) {
  const score = () => props.contentFitness?.overallScore ?? null;
  const state = () =>
    healthState(score(), PAGE_THRESHOLDS.good, PAGE_THRESHOLDS.watch);
  const coverage = () => {
    const contentFitness = props.contentFitness;
    if (!contentFitness) {
      return "Not available for this page";
    }
    return `${contentFitness.assessedSubcriteria} of ${contentFitness.canonicalSubcriteria} assessment criteria assessed (${coverageLabel(contentFitness.overallCoverageStatus)})`;
  };

  return (
    <aside class="scan-page-fitness-score" aria-label="Page Content Fitness">
      <span>Content Fitness</span>
      <div>
        <strong class="mono" style={{ color: healthVar(state()) }}>
          {score() ?? "--"}
        </strong>
        <small>/100</small>
      </div>
      <b>Assessment coverage</b>
      <p>{coverage()}</p>
    </aside>
  );
}

export function ContentFitnessDimensions(props: {
  contentFitness: PageContentFitness;
}) {
  return (
    <section class="scan-panel">
      <div class="scan-section-head">
        <div>
          <span class="scan-kicker">Content Fitness</span>
          <h2>Five dimensions of page fitness</h2>
          <p class="scan-muted">
            Scores use only persisted assessments. Missing and unable-to-verify
            criteria are not treated as zero.
          </p>
        </div>
      </div>
      <div class="signal-dimension-grid is-compact">
        <For each={pageContentFitnessDimensions(props.contentFitness)}>
          {(dimension) => {
            const unable = countByVerificationState(
              dimension.assessments,
              CONTENT_FITNESS_VERIFICATION_STATE.UNABLE_TO_VERIFY,
            );
            const notApplicable = countByVerificationState(
              dimension.assessments,
              CONTENT_FITNESS_VERIFICATION_STATE.NOT_APPLICABLE,
            );
            return (
              <article class="signal-dimension-card scan-page-dimension">
                <div>
                  <strong>{dimension.label}</strong>
                  <span class="mono">
                    {dimension.score === null
                      ? "No scored data"
                      : `${dimension.score}/100`}
                  </span>
                </div>
                <p>{dimension.description}</p>
                <small>
                  Scoring coverage: {dimension.assessedSubcriteria} of{" "}
                  {dimension.canonicalSubcriteria} criteria in the framework /{" "}
                  {coverageLabel(dimension.coverageStatus)}
                </small>
                <Show when={unable > 0}>
                  <small>{unable} unable to verify</small>
                </Show>
                <Show when={notApplicable > 0}>
                  <small>{notApplicable} not applicable</small>
                </Show>
              </article>
            );
          }}
        </For>
      </div>
    </section>
  );
}
